/**
 * selectedImport — the shape of the app-wide "current import" plus the pure
 * codec used to persist it. No React, no I/O: `SelectedImportContext.tsx`
 * owns the provider and the `localStorage` calls, this module owns the data.
 *
 * We store a PROJECTION of `ImportSummary`, not just the id, so the compact
 * selection bar can render immediately on a cold load, before (or without) any
 * round-trip.
 *
 * The projection is narrow — only fields the bar renders or a `requirements.ts`
 * predicate reads. It is NOT a snapshot you may trust indefinitely: `status`,
 * `totalContacts`, `tags` and the two display fields all drift server-side
 * while a selection sits in storage. `ImportSelectionBar` revalidates against
 * `GET /imports/{id}` on mount (and polls while the status is non-terminal), so
 * treat what is stored here as a first-paint placeholder, not the source of
 * truth.
 *
 * A previous revision merged the refresh over the stored value, keeping
 * `ownerLabel`/`searchName` because `get_import_detail` did not resolve them —
 * which meant a server-side unlink could never reach the bar. The endpoint now
 * resolves both (same PR), so this is a plain replace again. If you find
 * yourself reaching for a partial merge here, fix the endpoint instead.
 */

import type { ImportSummary, ImportTag } from "@nu-assist/contracts";

/** The canonical vocabularies, as literal tuples so a stored value can be
 *  membership-checked rather than merely type-asserted. Kept in lock-step with
 *  `ImportSummary` in `@nu-assist/contracts`; `satisfies` makes a drift in
 *  either direction a compile error. */
const FORMATS = [
  "baby_chops_v1",
  "bct",
  "ai_review",
] as const satisfies readonly ImportSummary["import_format"][];
const STATUSES = [
  "uploaded",
  "parsing",
  "ready",
  "failed",
] as const satisfies readonly ImportSummary["status"][];
const TAGS = [
  "raw",
  "bct",
  "ai",
  "enriched",
  "exported",
  "combined",
] as const satisfies readonly ImportTag[];

// The key and the external clear live in `lib/` so `clients/` can reach them
// without depending on a feature module; re-exported here so feature code
// keeps one import path.
export {
  SELECTED_IMPORT_STORAGE_KEY,
  clearStoredSelectedImport,
} from "../../../lib/selectedImportStorage";

export interface SelectedImport {
  importId: string;
  filename: string;
  totalContacts: number;
  status: ImportSummary["status"];
  tags: ImportTag[];
  /**
   * Spelled wire-style, unlike its camelCase neighbours, and that is the point:
   * `ImportUsability` is structural over BOTH a full `ImportSummary` and this
   * projection, so a field a usability rule reads has to carry one name on
   * both. `status` and `tags` above are here for the same reason.
   *
   * No usability rule reads it any more — `aiReviewUnusable` stopped when D5
   * was reversed (2026-08-25) — but `AiReviewPage` does: it is how the creator
   * knows the selected list is itself a review and its criteria should be
   * prefilled (Trello #711 cl.5).
   *
   * Optional so a selection stored before this field existed still validates
   * rather than being silently discarded on the next page load. Absent reads as
   * "not a review output", which stays the safe default: the worst it costs now
   * is an empty form where a prefilled one was possible.
   */
  import_format?: ImportSummary["import_format"];
  /** `owner_name` when resolved, else the uploader email. */
  ownerLabel: string;
  /**
   * The linked Thrive search id, or `null` when the import is unlinked.
   *
   * Carried alongside `searchName` because the bar links BOTH halves — the id
   * out to Thrive, the name in to Search Detail — and a name with no id can
   * only render as dead text. Added with those links; before them the bar
   * showed the name alone and had no use for the id.
   */
  searchIdThrive: string | null;
  searchName: string | null;
  createdAt: string;
}

/** Narrow a full list row down to the persisted projection. */
export function toSelectedImport(item: ImportSummary): SelectedImport {
  return {
    importId: item.import_id,
    filename: item.filename,
    totalContacts: item.total_contacts,
    status: item.status,
    tags: item.tags ?? [],
    import_format: item.import_format,
    ownerLabel: item.owner_name?.trim() ? item.owner_name : item.uploader_email,
    searchIdThrive: item.search_id_thrive ?? null,
    searchName: item.search_name ?? null,
    createdAt: item.created_at,
  };
}

function isNonEmptyString(v: unknown): v is string {
  return typeof v === "string" && v.length > 0;
}

/** Membership check against a canonical vocabulary — narrows without a cast. */
function isMemberOf<T extends string>(vocab: readonly T[], v: unknown): v is T {
  return typeof v === "string" && (vocab as readonly string[]).includes(v);
}

/**
 * Validate an untrusted stored value. Anything that is not a complete,
 * correctly-typed projection resolves to `null` — a half-restored selection
 * would render a bar with `undefined` in it and break the requirement
 * predicates, which is strictly worse than starting empty.
 *
 * `status` and `tags` are checked for MEMBERSHIP, not merely for being strings:
 * a stored `status: "bogus"` would otherwise survive the cast
 * and fall through `bctUnusable`'s else-branch as "still being processed",
 * which is exactly the kind of silent nonsense the versioned key exists to
 * prevent.
 */
export function parseStoredSelectedImport(raw: string | null): SelectedImport | null {
  if (!raw) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (typeof parsed !== "object" || parsed === null) return null;
  const v = parsed as Record<string, unknown>;

  if (!isNonEmptyString(v.importId)) return null;
  if (!isNonEmptyString(v.filename)) return null;
  if (typeof v.totalContacts !== "number" || !Number.isFinite(v.totalContacts)) return null;
  if (!isMemberOf(STATUSES, v.status)) return null;
  if (!Array.isArray(v.tags) || !v.tags.every((t) => isMemberOf(TAGS, t))) return null;
  if (v.import_format !== undefined && !isMemberOf(FORMATS, v.import_format)) return null;
  if (!isNonEmptyString(v.ownerLabel)) return null;
  if (v.searchIdThrive !== null && typeof v.searchIdThrive !== "string") return null;
  if (v.searchName !== null && typeof v.searchName !== "string") return null;
  if (!isNonEmptyString(v.createdAt)) return null;

  return {
    importId: v.importId,
    filename: v.filename,
    totalContacts: v.totalContacts,
    status: v.status,
    tags: v.tags as ImportTag[],
    import_format: v.import_format as ImportSummary["import_format"] | undefined,
    ownerLabel: v.ownerLabel,
    searchIdThrive: v.searchIdThrive,
    searchName: v.searchName,
    createdAt: v.createdAt,
  };
}

/**
 * True when two projections describe the same import with the same
 * bar-visible values. Used by the revalidation path to avoid a setState (and
 * a storage write) on every successful refetch.
 */
export function sameSelectedImport(a: SelectedImport, b: SelectedImport): boolean {
  return (
    a.importId === b.importId &&
    a.filename === b.filename &&
    a.totalContacts === b.totalContacts &&
    a.status === b.status &&
    a.ownerLabel === b.ownerLabel &&
    a.searchIdThrive === b.searchIdThrive &&
    a.searchName === b.searchName &&
    a.createdAt === b.createdAt &&
    a.tags.length === b.tags.length &&
    a.tags.every((t, i) => t === b.tags[i])
  );
}
